/**
 * Todo domain helpers (daemon `/todos`): untrusted wire parsing, draft/form
 * validation, sorting and the demo seed.
 *
 * The daemon is authoritative: the PWA only creates/updates rows through the
 * API and always renders the row the daemon returned. Nothing here mutates
 * state; a malformed row is dropped and a malformed response envelope returns
 * `null` so the client can surface a real error instead of inventing data.
 */
import type { Todo, TodoPriority, TodoStatus } from './types';
import { parseDatetimeLocal, datetimeLocalFromSeconds, isoTimestampFromSeconds } from './datetime';
import {
  cappedRawText,
  cappedText,
  isEntityId,
  isRecord,
  normalizedTimestamp,
  optionalTimestamp,
} from './validation';

export const TODO_TITLE_MAX_CHARS = 200;
export const TODO_NOTES_MAX_CHARS = 2000;
export const TODO_MAX_ITEMS = 5000;

export const TODO_PRIORITY_OPTIONS: ReadonlyArray<{ value: TodoPriority; label: string }> = [
  { value: 'low', label: '低' },
  { value: 'normal', label: '普通' },
  { value: 'high', label: '高' },
];

export const TODO_PRIORITY_LABELS: Record<TodoPriority, string> = {
  low: '低优先级',
  normal: '普通',
  high: '高优先级',
};

export const TODO_STATUS_LABELS: Record<TodoStatus, string> = {
  open: '待办',
  done: '已完成',
};

const PRIORITY_RANK: Record<TodoPriority, number> = { high: 0, normal: 1, low: 2 };

export function isTodoPriority(value: unknown): value is TodoPriority {
  return value === 'low' || value === 'normal' || value === 'high';
}

export function normalPriority(value: unknown): TodoPriority {
  if (value === 'low' || value === 'high') return value;
  // Daemon/API variants degrade to the neutral priority instead of dropping a row.
  if (value === 'urgent' || value === 'critical') return 'high';
  return 'normal';
}

export function normalTodoStatus(value: unknown): TodoStatus {
  return value === 'done' || value === 'completed' || value === 'closed' ? 'done' : 'open';
}

function capTitle(value: string): string {
  return cappedText(value, TODO_TITLE_MAX_CHARS);
}

function capNotes(value: string): string {
  const trimmed = value.trim();
  return trimmed.length === 0 ? '' : cappedRawText(trimmed, TODO_NOTES_MAX_CHARS);
}

/** Parse one untrusted todo row; invalid rows are dropped by the caller. */
export function todoFromUnknown(value: unknown): Todo | null {
  if (!isRecord(value)) return null;
  if (!isEntityId(value.id)) return null;
  if (typeof value.title !== 'string') return null;
  const title = capTitle(value.title);
  if (title.length === 0) return null;
  if (value.notes !== undefined && value.notes !== null && typeof value.notes !== 'string') {
    return null;
  }
  const notes = typeof value.notes === 'string' ? capNotes(value.notes) : '';
  const status = normalTodoStatus(value.status);
  const createdAt = normalizedTimestamp(value.created_at);
  const updatedAt = normalizedTimestamp(value.updated_at) || createdAt;
  return {
    id: value.id,
    title,
    notes,
    due_at: optionalTimestamp(value.due_at),
    remind_at: optionalTimestamp(value.remind_at),
    priority: normalPriority(value.priority),
    status,
    created_at: createdAt,
    updated_at: updatedAt,
    completed_at: optionalTimestamp(value.completed_at),
  };
}

/**
 * Parse a `/todos` response. Accepts `{ todos: [...] }` (contract) and a bare
 * array (defensive). Returns `null` only when the envelope is invalid; bad rows
 * are dropped and duplicate ids collapse (last row wins).
 */
export function todosFromUnknown(value: unknown): Todo[] | null {
  const rawList = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.todos)
      ? value.todos
      : null;
  if (rawList === null) return null;
  const todos = new Map<string, Todo>();
  for (const raw of rawList) {
    const todo = todoFromUnknown(raw);
    if (todo !== null) todos.set(todo.id, todo);
  }
  return sortTodos([...todos.values()]).slice(0, TODO_MAX_ITEMS);
}

export function todoPriorityLabel(priority: TodoPriority): string {
  return TODO_PRIORITY_LABELS[priority];
}

export function todoStatusLabel(status: TodoStatus): string {
  return TODO_STATUS_LABELS[status];
}

export function isTodoOverdue(todo: Todo, now: number): boolean {
  return todo.status === 'open' && todo.due_at !== null && todo.due_at <= now;
}

/** Open first, then due (nearest first, no due last), priority, newest update. */
export function sortTodos(todos: readonly Todo[]): Todo[] {
  return [...todos].sort((left, right) => {
    if (left.status !== right.status) return left.status === 'open' ? -1 : 1;
    if (left.status === 'open') {
      const leftDue = left.due_at ?? Number.POSITIVE_INFINITY;
      const rightDue = right.due_at ?? Number.POSITIVE_INFINITY;
      if (leftDue !== rightDue) return leftDue - rightDue;
      const byPriority = PRIORITY_RANK[left.priority] - PRIORITY_RANK[right.priority];
      if (byPriority !== 0) return byPriority;
    }
    return right.updated_at - left.updated_at;
  });
}

export interface PartitionedTodos {
  open: Todo[];
  done: Todo[];
  overdue: Todo[];
}

export function partitionTodos(todos: readonly Todo[], now: number): PartitionedTodos {
  const sorted = sortTodos(todos);
  const open = sorted.filter((todo) => todo.status === 'open');
  return {
    open,
    done: sorted.filter((todo) => todo.status === 'done'),
    overdue: open.filter((todo) => isTodoOverdue(todo, now)),
  };
}

export interface TodoCounts {
  total: number;
  open: number;
  done: number;
  overdue: number;
}

export function todoCounts(todos: readonly Todo[], now: number): TodoCounts {
  const overdue = todos.filter((todo) => isTodoOverdue(todo, now)).length;
  const done = todos.filter((todo) => todo.status === 'done').length;
  return { total: todos.length, open: todos.length - done, done, overdue };
}

/** Insert/replace a row (used only after a successful daemon mutation). */
export function upsertTodo(todos: readonly Todo[], row: Todo): Todo[] {
  const index = todos.findIndex((todo) => todo.id === row.id);
  if (index < 0) return sortTodos([...todos, row]);
  return sortTodos(todos.map((todo) => (todo.id === row.id ? row : todo)));
}

// ---------------------------------------------------------------------------
// Drafts / forms
// ---------------------------------------------------------------------------

export interface TodoDraft {
  title: string;
  notes: string;
  due_at: number | null;
  remind_at: number | null;
  priority: TodoPriority;
}

export type TodoPatch = Partial<TodoDraft>;

export interface TodoFormValues {
  title: string;
  notes: string;
  due_local: string;
  remind_local: string;
  priority: TodoPriority;
}

export type TodoField = 'title' | 'notes' | 'due_local' | 'remind_local' | 'priority';
export type TodoFormErrors = Partial<Record<TodoField, string>>;

export type TodoDraftValidation = { ok: true; value: TodoDraft } | { ok: false; message: string };

export type TodoFormValidation =
  { ok: true; value: TodoDraft } | { ok: false; errors: TodoFormErrors; message: string };

export function emptyTodoForm(): TodoFormValues {
  return { title: '', notes: '', due_local: '', remind_local: '', priority: 'normal' };
}

export function todoFormValuesFromTodo(todo: Todo): TodoFormValues {
  return {
    title: todo.title,
    notes: todo.notes,
    due_local: datetimeLocalFromSeconds(todo.due_at),
    remind_local: datetimeLocalFromSeconds(todo.remind_at),
    priority: todo.priority,
  };
}

/** Validate the CRUD form (datetime-local strings) before touching the client. */
export function validateTodoForm(values: TodoFormValues): TodoFormValidation {
  const errors: TodoFormErrors = {};
  const title = capTitle(values.title);
  if (title.length === 0) errors.title = '标题不能为空';
  const due = parseDatetimeLocal(values.due_local);
  if (!due.ok) errors.due_local = '截止时间格式无效';
  const remind = parseDatetimeLocal(values.remind_local);
  if (!remind.ok) errors.remind_local = '提醒时间格式无效';
  if (!isTodoPriority(values.priority)) errors.priority = '优先级无效';
  if (Object.keys(errors).length > 0) {
    return { ok: false, errors, message: '请检查待办内容后重试' };
  }
  return {
    ok: true,
    value: {
      title,
      notes: capNotes(values.notes),
      due_at: due.seconds,
      remind_at: remind.seconds,
      priority: values.priority,
    },
  };
}

function draftTimestamp(
  value: unknown,
  field: string,
): { ok: true; value: number | null } | { ok: false; message: string } {
  if (value === undefined || value === null) return { ok: true, value: null };
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return { ok: false, message: `${field}必须是有效的秒级时间戳` };
  }
  return { ok: true, value: optionalTimestamp(value) };
}

/** Runtime validation for a create payload (also used by the mock client). */
export function normalizeTodoDraft(value: unknown): TodoDraftValidation {
  if (!isRecord(value)) return { ok: false, message: '待办内容无效' };
  if (typeof value.title !== 'string' || capTitle(value.title).length === 0) {
    return { ok: false, message: '待办标题不能为空' };
  }
  if (value.notes !== undefined && value.notes !== null && typeof value.notes !== 'string') {
    return { ok: false, message: '待办备注必须是文本' };
  }
  const due = draftTimestamp(value.due_at, '截止时间');
  if (!due.ok) return due;
  const remind = draftTimestamp(value.remind_at, '提醒时间');
  if (!remind.ok) return remind;
  if (value.priority !== undefined && !isTodoPriority(value.priority)) {
    return { ok: false, message: '待办优先级无效' };
  }
  return {
    ok: true,
    value: {
      title: capTitle(value.title),
      notes: typeof value.notes === 'string' ? capNotes(value.notes) : '',
      due_at: due.value,
      remind_at: remind.value,
      priority: value.priority === undefined ? 'normal' : (value.priority as TodoPriority),
    },
  };
}

export type TodoPatchValidation = { ok: true; value: TodoPatch } | { ok: false; message: string };

/** Runtime validation for a PATCH body; only provided fields are returned. */
export function normalizeTodoPatch(value: unknown): TodoPatchValidation {
  if (!isRecord(value)) return { ok: false, message: '待办更新内容无效' };
  const patch: TodoPatch = {};
  if (value.title !== undefined) {
    if (typeof value.title !== 'string' || capTitle(value.title).length === 0) {
      return { ok: false, message: '待办标题不能为空' };
    }
    patch.title = capTitle(value.title);
  }
  if (value.notes !== undefined) {
    if (value.notes !== null && typeof value.notes !== 'string') {
      return { ok: false, message: '待办备注必须是文本' };
    }
    patch.notes = typeof value.notes === 'string' ? capNotes(value.notes) : '';
  }
  if (value.due_at !== undefined) {
    const due = draftTimestamp(value.due_at, '截止时间');
    if (!due.ok) return due;
    patch.due_at = due.value;
  }
  if (value.remind_at !== undefined) {
    const remind = draftTimestamp(value.remind_at, '提醒时间');
    if (!remind.ok) return remind;
    patch.remind_at = remind.value;
  }
  if (value.priority !== undefined) {
    if (!isTodoPriority(value.priority)) return { ok: false, message: '待办优先级无效' };
    patch.priority = value.priority;
  }
  return { ok: true, value: patch };
}

/** `POST /todos` body (snake_case + ISO 8601 timestamps on the wire). */
export function todoCreateBody(draft: TodoDraft): Record<string, unknown> {
  return {
    title: draft.title,
    notes: draft.notes,
    due_at: isoTimestampFromSeconds(draft.due_at),
    remind_at: isoTimestampFromSeconds(draft.remind_at),
    priority: draft.priority,
  };
}

/** `PATCH /todos/:id` body: only fields the caller actually changed. */
export function todoPatchBody(patch: TodoPatch): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (patch.title !== undefined) body.title = patch.title;
  if (patch.notes !== undefined) body.notes = patch.notes;
  if (patch.due_at !== undefined) body.due_at = isoTimestampFromSeconds(patch.due_at);
  if (patch.remind_at !== undefined) body.remind_at = isoTimestampFromSeconds(patch.remind_at);
  if (patch.priority !== undefined) body.priority = patch.priority;
  return body;
}

/**
 * Demo-only seed for the mock client. Clearly labelled as mock in the UI and
 * only used when `settings.demoMode` is on.
 */
export function demoTodos(now: number): Todo[] {
  return [
    {
      id: 'todo_demo_report',
      title: '提交本周周报',
      notes: '汇总 Luminis PWA 的待办/闹钟进度，附测试结果。',
      due_at: now - 2 * 3600,
      remind_at: now - 3 * 3600,
      priority: 'high',
      status: 'open',
      created_at: now - 2 * 86_400,
      updated_at: now - 3 * 3600,
      completed_at: null,
    },
    {
      id: 'todo_demo_alarm',
      title: '确认闹钟与原生桥接',
      notes: '在 Android 壳中注入 window.LuminisNativeAlarms 后验证调度/取消。',
      due_at: now + 3 * 3600,
      remind_at: now + 2 * 3600,
      priority: 'normal',
      status: 'open',
      created_at: now - 86_400,
      updated_at: now - 3600,
      completed_at: null,
    },
    {
      id: 'todo_demo_review',
      title: '整理待办清单',
      notes: '',
      due_at: null,
      remind_at: null,
      priority: 'low',
      status: 'open',
      created_at: now - 7200,
      updated_at: now - 7200,
      completed_at: null,
    },
    {
      id: 'todo_demo_done',
      title: '修复 PWA 断线重连',
      notes: '已完成并通过离线测试。',
      due_at: now - 86_400,
      remind_at: null,
      priority: 'normal',
      status: 'done',
      created_at: now - 3 * 86_400,
      updated_at: now - 86_400,
      completed_at: now - 86_400,
    },
  ];
}
